import {
  ACOES,
  RECURSOS,
  codigoDePapelSchema,
  permissaoSchema,
  type CodigoDePapel,
  type Permissao,
} from '@locaveri/shared'
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'
import { z } from 'zod'

import { apiGet, apiPut } from '@/lib/api'
import { ROTAS_DE_RBAC } from '@/lib/endpoints'

/**
 * PAPÉIS E PERMISSÕES — o que cada perfil acessa, editável sem deploy.
 *
 * ===========================================================================
 * A CONCESSÃO NUNCA FOI CÓDIGO
 * ===========================================================================
 * O CLAUDE.md manda permissão por recurso+ação em TABELA, e é assim desde a
 * Fase 1: `role_permissions` é a verdade, e `PERMISSOES_POR_PAPEL` do pacote
 * compartilhado é só o padrão que o seed instala. A API já expunha a edição
 * (`PUT /rbac/roles/:codigo/permissions`) e nenhuma tela a dirigia — o gestor
 * tinha o poder e não tinha o botão.
 *
 * ===========================================================================
 * OS DOIS 403 QUE A TELA PRECISA ANTECIPAR
 * ===========================================================================
 * A API recusa duas coisas, e as duas precisam aparecer ANTES do clique, senão
 * viram erro sem causa aparente no meio de uma edição longa:
 *
 *   1. CONCEDER O QUE VOCÊ NÃO TEM. Ninguém amplia o próprio alcance por
 *      interposta pessoa — criar um papel com `payment.export`, atribuí-lo a si
 *      mesmo e exportar. A tela desabilita essas caixas com o motivo.
 *   2. EDITAR O SUPER_ADMIN. É o único papel capaz de reabrir o controle de
 *      acesso; deixá-lo editável permite trancar a instalação inteira do lado
 *      de fora, sem caminho de volta pela interface.
 */

// ---------------------------------------------------------------------------
// Contratos de leitura — espelham `rbac.dto.ts` da API
// ---------------------------------------------------------------------------

export const permissaoDaApiSchema = z.object({
  chave: permissaoSchema,
  recurso: z.enum(RECURSOS),
  acao: z.enum(ACOES),
  descricao: z.string(),
})

export type PermissaoDaApi = z.infer<typeof permissaoDaApiSchema>

export const papelDaApiSchema = z.object({
  id: z.uuid(),
  codigo: codigoDePapelSchema,
  nome: z.string(),
  descricao: z.string(),
  ehInterno: z.boolean(),
  /** `false` só para `VISITANTE` — resolvido pelo token da sessão anônima. */
  atribuivel: z.boolean(),
  exige2fa: z.boolean(),
  /** A concessão de HOJE, da tabela — não o catálogo em código. */
  permissoes: z.array(permissaoSchema),
  usuarios: z.number().int().nonnegative(),
})

export type PapelDaApi = z.infer<typeof papelDaApiSchema>

const listaDePapeisSchema = z.object({ itens: z.array(papelDaApiSchema) })
const listaDePermissoesSchema = z.object({ itens: z.array(permissaoDaApiSchema) })

export const CHAVE_DE_PAPEIS = ['rbac', 'papeis'] as const
export const CHAVE_DE_PERMISSOES = ['rbac', 'permissoes'] as const

export function usePapeis(): UseQueryResult<{ readonly itens: readonly PapelDaApi[] }> {
  return useQuery({
    queryKey: CHAVE_DE_PAPEIS,
    queryFn: ({ signal }) => apiGet(ROTAS_DE_RBAC.papeis, listaDePapeisSchema, { signal }),
  })
}

export function useCatalogoDePermissoes(): UseQueryResult<{
  readonly itens: readonly PermissaoDaApi[]
}> {
  return useQuery({
    queryKey: CHAVE_DE_PERMISSOES,
    // O catálogo é praticamente estático: ele só muda quando o CÓDIGO muda.
    // Uma hora de frescor evita uma ida à rede a cada abertura da tela.
    staleTime: 60 * 60 * 1000,
    queryFn: ({ signal }) => apiGet(ROTAS_DE_RBAC.permissoes, listaDePermissoesSchema, { signal }),
  })
}

export interface EdicaoDeConcessao {
  readonly codigo: CodigoDePapel
  readonly permissoes: readonly Permissao[]
}

/**
 * Substitui a concessão de um papel.
 *
 * `PUT` e não `PATCH`: a lista enviada passa a SER a concessão. O corpo não é
 * delta, e tratá-lo como tal é como se apaga uma permissão sem ninguém pedir.
 *
 * A invalidação inclui a sessão: quem edita o PRÓPRIO papel muda as próprias
 * permissões, e o menu lateral — que se desenha a partir delas — ficaria
 * mostrando itens que a API já recusa.
 */
export function useDefinirPermissoesDoPapel(): UseMutationResult<
  PapelDaApi,
  Error,
  EdicaoDeConcessao
> {
  const cliente = useQueryClient()

  return useMutation({
    mutationFn: (edicao: EdicaoDeConcessao) =>
      apiPut(ROTAS_DE_RBAC.permissoesDoPapel(edicao.codigo), papelDaApiSchema, {
        permissoes: edicao.permissoes,
      }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: CHAVE_DE_PAPEIS })
    },
  })
}
